import { useCallback, useEffect, useId, useState } from 'react'
import {
  formatRouletteProbabilityPercentBr,
  parseRouletteProbabilityForLiveUpdate,
  parseRouletteProbabilityPercentInput,
  parseRouletteProbabilityPercentOnBlur,
  ROULETTE_PROBABILITY_DECIMAL_PLACES,
  sanitizeRouletteProbabilityInput,
} from '@/lib/rouletteProbability'
import styles from './CampanhasPage.module.css'

export interface RoletaProbabilityInputProps {
  id?: string
  value: number
  onChange: (value: number) => void
  disabled?: boolean
  describedBy?: string
}

export function RoletaProbabilityInput({
  id: idProp,
  value,
  onChange,
  disabled = false,
  describedBy,
}: RoletaProbabilityInputProps) {
  const autoId = useId()
  const inputId = idProp ?? autoId
  const [draft, setDraft] = useState(() => formatRouletteProbabilityPercentBr(value))
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (!isFocused) {
      setDraft(formatRouletteProbabilityPercentBr(value))
    }
  }, [value, isFocused])

  const commitDraft = useCallback(
    (raw: string) => {
      const parsed = parseRouletteProbabilityPercentOnBlur(raw)
      if (parsed === null) {
        onChange(0)
        setDraft(formatRouletteProbabilityPercentBr(0))
        return
      }
      onChange(parsed)
      setDraft(formatRouletteProbabilityPercentBr(parsed))
    },
    [onChange]
  )

  const shouldFormatDraftWhileTyping = useCallback((sanitized: string): boolean => {
    if (sanitized.endsWith(',')) {
      return false
    }
    const fraction = sanitized.split(',')[1]
    if (fraction !== undefined && fraction.length < ROULETTE_PROBABILITY_DECIMAL_PLACES) {
      return false
    }
    return parseRouletteProbabilityPercentInput(sanitized) !== null
  }, [])

  const handleChange = useCallback(
    (raw: string) => {
      const sanitized = sanitizeRouletteProbabilityInput(raw)
      if (sanitized === '') {
        setDraft('')
        onChange(0)
        return
      }
      const parsed = parseRouletteProbabilityForLiveUpdate(sanitized)
      if (parsed !== null) {
        onChange(parsed)
        setDraft(
          shouldFormatDraftWhileTyping(sanitized)
            ? formatRouletteProbabilityPercentBr(parsed)
            : sanitized
        )
        return
      }
      setDraft(sanitized)
    },
    [onChange, shouldFormatDraftWhileTyping]
  )

  const handleBlur = useCallback(() => {
    setIsFocused(false)
    commitDraft(draft)
  }, [commitDraft, draft])

  return (
    <>
      <input
        id={inputId}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        className={styles.input}
        value={draft}
        aria-describedby={describedBy}
        placeholder="0"
        onFocus={() => setIsFocused(true)}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
      />
    </>
  )
}
